import React, { Component } from 'react';
import { Context } from 'context';

import Highstocks from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import CustomEvents from 'highcharts-custom-events';
import hexRgb from 'hex-rgb';
import Mousetrap from 'mousetrap';
import equals from "fast-deep-equal";
import clone from 'clone-deep';

import styles from './HexViewer.module.scss';
import vars_dark from "styles/_vars-dark.scss";
import vars_light from "styles/_vars-light.scss";
import sortObjectsArray from "sort-objects-array";

CustomEvents(Highstocks);

class HexViewer extends Component {
    static contextType = Context;

    chart = React.createRef();

    state = {
        focusedMapId: "",
        disableActions: true,
        loading: true,
        range: 1500,
        mode: 4,
        signed: false,
        loHi: true,
        chartOptions: {
            chart: ""
        },
        colors: {
            "ac control": this.context.lightTheme ? vars_light.ACControl : vars_dark.ACControl,
            "air monitoring + egr": this.context.lightTheme ? vars_light.AirMonitoring : vars_dark.AirMonitoring,
            "airbag interface": this.context.lightTheme ? vars_light.AirbagInterface : vars_dark.AirbagInterface,
            "body interface": this.context.lightTheme ? vars_light.BodyInterface : vars_dark.BodyInterface,
            "can bus": this.context.lightTheme ? vars_light.CANBus : vars_dark.CANBus,
            "chassis control + vmax": this.context.lightTheme ? vars_light.ChassisControlVMAX : vars_dark.ChassisControlVMAX,
            "coolant management": this.context.lightTheme ? vars_light.CoolantManagement : vars_dark.CoolantManagement,
            "cruise control": this.context.lightTheme ? vars_light.CruiseControl : vars_dark.CruiseControl,
            "cylinder pressure": this.context.lightTheme ? vars_light.CylinderPressure : vars_dark.CylinderPressure,
            "diagnostics + configuration": this.context.lightTheme ? vars_light.DiagnosticsConfiguration : vars_dark.DiagnosticsConfiguration,
            "emission control": this.context.lightTheme ? vars_light.EmissionControl : vars_dark.EmissionControl,
            "emission control - dpf": this.context.lightTheme ? vars_light.EmissionControlDPF : vars_dark.EmissionControlDPF,
            "exhaust gas monitoring": this.context.lightTheme ? vars_light.ExhaustGasMonitoring : vars_dark.ExhaustGasMonitoring,
            "function monitoring": this.context.lightTheme ? vars_light.FunctionMonitoring : vars_dark.FunctionMonitoring,
            "idle control": this.context.lightTheme ? vars_light.IdleControl : vars_dark.IdleControl,
            "ignition timing": this.context.lightTheme ? vars_light.IgnitionTiming : vars_dark.IgnitionTiming,
            "injection control": this.context.lightTheme ? vars_light.InjectionControl : vars_dark.InjectionControl,
            "injection control hpfp": this.context.lightTheme ? vars_light.InjectionControlHPFP : vars_dark.InjectionControlHPFP,
            "injection curve": this.context.lightTheme ? vars_light.InjectionCurve : vars_dark.InjectionCurve,
            "injection valve": this.context.lightTheme ? vars_light.InjectionValve : vars_dark.InjectionValve,
            "injection valve component driver": this.context.lightTheme ? vars_light.InjectionValveComponentDriver : vars_dark.InjectionValveComponentDriver,
            "knock control": this.context.lightTheme ? vars_light.KnockControl : vars_dark.KnockControl,
            "lambda control": this.context.lightTheme ? vars_light.LambdaControl : vars_dark.LambdaControl,
            "motor protection": this.context.lightTheme ? vars_light.MotorProtection : vars_dark.MotorProtection,
            "obd diagnostics": this.context.lightTheme ? vars_light.OBDDiagnostics : vars_dark.OBDDiagnostics,
            "overrun monitoring": this.context.lightTheme ? vars_light.OverrunMonitoring : vars_dark.OverrunMonitoring,
            "power latch": this.context.lightTheme ? vars_light.PowerLatch : vars_dark.PowerLatch,
            "pre heating - start up control": this.context.lightTheme ? vars_light.PreHeatingStartUpControl : vars_dark.PreHeatingStartUpControl,
            "propulsion control": this.context.lightTheme ? vars_light.PropulsionControl : vars_dark.PropulsionControl,
            "rail pressure control": this.context.lightTheme ? vars_light.RailPressureControl : vars_dark.RailPressureControl,
            "sensor drivers": this.context.lightTheme ? vars_light.SensorDrivers : vars_dark.SensorDrivers,
            "swirl control": this.context.lightTheme ? vars_light.SwirlControl : vars_dark.SwirlControl,
            "thermal control": this.context.lightTheme ? vars_light.ThermalControl : vars_dark.ThermalControl,
            "throttle control": this.context.lightTheme ? vars_light.ThrottleControl : vars_dark.ThrottleControl,
            "torque monitoring": this.context.lightTheme ? vars_light.TorqueMonitoring : vars_dark.TorqueMonitoring,
            "transmission monitoring": this.context.lightTheme ? vars_light.TransmissionMonitoring : vars_dark.TransmissionMonitoring,
            "turbine control": this.context.lightTheme ? vars_light.TurbineControl : vars_dark.TurbineControl,
            "valvetrain": this.context.lightTheme ? vars_light.Valvetrain : vars_dark.Valvetrain,

            "config": this.context.lightTheme ? vars_light.config : vars_dark.config,
            "torque": this.context.lightTheme ? vars_light.torque : vars_dark.torque,
            "timing": this.context.lightTheme ? vars_light.timing : vars_dark.timing,
            "thermal": this.context.lightTheme ? vars_light.thermal : vars_dark.thermal,
            "fuel": this.context.lightTheme ? vars_light.fuel : vars_dark.fuel,
            "air": this.context.lightTheme ? vars_light.air : vars_dark.air,
            "limits": this.context.lightTheme ? vars_light.limits : vars_dark.limits,
            "ungrouped": this.context.lightTheme ? vars_light.ungrouped : vars_dark.ungrouped
        },
        navigatorHeight: clone(this.props.navigatorHeight)
    };

    shouldComponentUpdate(nextProps, nextState) {
        return true;
    }

    componentDidMount(){
        this.initialiseKeyBindings();

        const calibrationZones = this.initialiseCalibrationZones(this.context.folderStructure);
        let navigatorBands = this.initialiseNavigatorBands(calibrationZones);

        let maps = this.getMaps();
        let graphBands = this.initialiseGraphBands(calibrationZones, maps);

        // these events are defined on hexview.js and can't use mousetrap due to scroll.
        window.addEventListener('wheel', this.handleScroll);

        this.setState({
            calibrationZones,
            graphBands,
            chartOptions: this.initialiseChart(graphBands, navigatorBands),
            disableActions: false,
            loading: false
        }, () => {
            const focusedMapId = this.props.selectedMapId
            this.setFocusedMapId(focusedMapId);
        });
    }

    componentDidUpdate(prevProps, prevState, snapshot) {

        if(prevProps.modifiedCalibrationData !== this.props.modifiedCalibrationData) this.updateEdited();

        // if(!this.state.loading && prevProps.selectedMapId !== this.props.selectedMapId) this.setFocusedMapId(this.props.selectedMapId);

        if(!this.state.loading && prevProps.updateHexToFrom !== this.props.updateHexToFrom) this.setFocusedMapId(this.props.selectedMapId);
        if(!this.state.loading && this.props.windows.length > prevProps.windows.length && this.props.windows.length !== 0) this.setFocusedMapId(this.props.selectedMapId);

        if(!this.state.loading && !equals(prevProps.windows, this.props.windows)) {
            this.updateView(this.chart.current.chart.xAxis[0].min, this.chart.current.chart.xAxis[0].max);
        }

        if(!equals(prevProps.modifiedMaps, this.props.modifiedMaps)) this.updateView(this.chart.current.chart.xAxis[0].min, this.chart.current.chart.xAxis[0].max);

        if(prevState.signed !== this.state.signed) {
            this.chart.current.chart.update({
                series: [{
                    data: this.formatData(this.state.mode).edited
                }, {
                    data: this.formatData(this.state.mode).formatted
                }]
            })
        }

        if(prevState.loHi !== this.state.loHi) {
            this.chart.current.chart.update({
                series: [{
                    data: this.formatData(this.state.mode).edited
                }, {
                    data: this.formatData(this.state.mode).formatted
                }]
            })
        }

        if(this.props.navigatorHeight !== this.chart.current.chart.height) {
            this.chart.current.chart.update({
                chart: {
                    height: this.props.navigatorHeight
                }
            });
        }
    }


    /**
     * Initialise
     */

    initialiseKeyBindings() {
        Mousetrap.bind(this.context.text.shortcuts.openMap.keys, this.openMap);
        Mousetrap.bind(this.context.text.shortcuts.toPreviousMap.keys, this.toPreviousMap);
        Mousetrap.bind(this.context.text.shortcuts.toNextMap.keys, this.toNextMap);
        Mousetrap.bind(this.context.text.shortcuts.toNextEditedMap.keys, this.toNextEditedMap);
        Mousetrap.bind(this.context.text.shortcuts.toPreviousEditedMap.keys, this.toPreviousEditedMap);
    }

    initialiseCalibrationZones = (folderStructure) => {
        let calibrationZones = [];

        folderStructure.forEach((folder) => {
            folder.codeNames.forEach((map) => {
                calibrationZones.push({
                    id: map.id,
                    name: map.titleDescription,
                    groupName: folder.groupName,
                    from: parseInt(map.calibrationStartAddress),
                    to: parseInt(map.calibrationEndAddress)
                });
            })
        });

        return sortObjectsArray(calibrationZones, "from");
    };

    initialiseNavigatorBands(calibrationZones){

        sortObjectsArray(calibrationZones, "from");

        const threshold = 100;
        let navigatorBands = [];

        calibrationZones.forEach((zone, i) => {

            zone.to = zone.to / 2;
            zone.from = zone.from / 2;
            let color = hexRgb('#434343');
            if (this.state.colors[zone.groupName.toLowerCase()]) {
                color = hexRgb(this.state.colors[zone.groupName.toLowerCase()]);
            }
            //console.log('Nav Band!');
            //console.log(zone.groupName.toLowerCase());
            //console.log(color);
            if(i === 0){
                navigatorBands.push({
                    from: zone.from,
                    to: zone.to,
                    color:  `rgba(${color.red}, ${color.green}, ${color.blue}, 0.8)`
                })
            } else {

                const band = navigatorBands[navigatorBands.length - 1];

                const lowerThreshold = band.from - threshold;
                const upperThreshold = band.to + threshold;

                let match = false;

                if((zone.to >= lowerThreshold && zone.to <= upperThreshold) || (zone.from >= lowerThreshold && zone.from <= upperThreshold)){
                    navigatorBands[navigatorBands.length - 1] = {
                        ...band,
                        to: zone.to,
                        zIndex: 0
                    };

                    match = true;
                }

                if(!match) navigatorBands.push({
                    from: zone.from,
                    to: zone.to,
                    color:  `rgba(${color.red}, ${color.green}, ${color.blue}, 0.8)`
                })
            }
        });

        return navigatorBands;
    }

    initialiseGraphBands = (calibrationZones, maps) => {

        return calibrationZones.map((band, i) => {

            //console.log(this.state.colors[band.groupName.toLowerCase()]);
            let color = hexRgb('#434343');

            if (this.state.colors[band.groupName.toLowerCase()]) {
                //console.log(band.groupName.toLowerCase());
                //this needs looking at for the hex viewer
                //console.log(band.groupName.toLowerCase());
                color = hexRgb(band.groupName.toLowerCase() ? this.state.colors[band.groupName.toLowerCase()] : this.state.colors.default); // todo - save everything as rgb to save doing this
            }

            //console.log('Graph Band!');
            //console.log(color);
            const map = maps.filter((m) => { return m.id === band.id })[0];

            const groupNameCapitalised = band.groupName.charAt(0).toUpperCase() + band.groupName.slice(1);

            return {
                id: map.id,
                title: map.title,
                titleDescription: map.titleDescription,
                mapSize: map.mapSize,
                type: map.type,
                borderColor: "white",
                borderWidth: 0,
                label: {
                    useHTML: true,
                    text: `<div class="${styles.info} bgTertiary highlightSecondary"><p><strong>Code Name:</strong> ${map.title}</p><p><strong>Description:</strong> ${map.titleDescription}</p><p><strong>Group Name:</strong> ${groupNameCapitalised}</p><p><strong>Type:</strong> ${map.type}</p><p><strong>Size: </strong> ${map.mapSize}</p></div>`,
                    style: {
                        display: 'none'
                    },
                    className: `label-${band.id}`,
                },
                from: band.from,
                to: band.to,
                color: clone(i % 2 ? `rgba(${color.red}, ${color.green}, ${color.blue}, 0.1)` : `rgba(${color.red}, ${color.green}, ${color.blue}, 0.2)`),
                width: 1,
                className: `pointer map-${band.id}`,
                zIndex: 20,
                events: {
                    dblclick: (event) => {
                        let target = event.target;
                        if(event.touches) target = document.elementFromPoint(event.touches[0].clientX, event.touches[0].clientY);

                        if(target){
                            target.className.baseVal.split(" ").forEach((className) => { // todo - this is fucking awful
                                className = className.split('map-');
                                if(className.length === 2){
                                    this.context.openWindow(className[1]);
                                }
                            })
                        }
                    },
                    mouseover: () => {
                        document.getElementsByClassName(`label-${map.id}`)[0].style.display = "block";
                    },
                    mouseout: () => {
                        document.getElementsByClassName(`label-${map.id}`)[0].style.display = "none";
                    }
                },
            }
        });
    };

    initialiseChart = (graphBands, navigatorBands) => {
        const min = this.bitConvert(10, this.state.mode, this.state.mode);
        const max = this.calculateMax(min);
        const formatted = this.formatData(this.state.mode).formatted;
        const edited = this.formatData(this.state.mode).edited;

        return {
            chart: {
                alignTicks: false,
                className: "chart",
                backgroundColor: 'rgba(0,0,0,0)',
                spacingTop: 0,
                spacingRight: 0,
                spacingBottom: 0,
                spacingLeft: 0,
                plotBorderWidth: 0,
                margin: [0,0,0,0],
                animation: false,
                panning: {
                    enabled: true
                },
                height: this.props.navigatorHeight
            },
            plotOptions: {
                series: {
                    animation: false,
                    dataGrouping: {
                        groupPixelWidth: 0,
                    },
                    cursor: "pointer",
                    turboThreshold: 0,
                    states: {
                        inactive: {
                            opacity: 1
                        }
                    }
                }
            },
            colors: [this.context.lightTheme ? vars_light.txtSecondary : vars_dark.txtPrimary],
            rangeSelector: {
                enabled: false
            },
            navigator: {
                enabled: true,
                series: {
                    type: 'line',
                    lineWidth: 0,
                    marker: {
                        enabled: false
                    },
                },
                xAxis: {
                    visible: true,
                    zoomEnabled: false,
                    labels: {
                        enabled: false
                    },
                    gridLineWidth: 0,
                    plotBands: navigatorBands.map((bands) => {
                        return {
                            ...bands,
                            from: bands.from,
                            to: bands.to,
                        }
                    }),
                },
                yAxis: {
                    visible: false,
                },
                maskFill: this.context.lightTheme ? "rgba(72, 72, 72, 0.5)" : "rgba(255,255,255,0.5)",
                outlineWidth: 1,
                outlineColor: this.context.lightTheme ? vars_light.bgTertiary : vars_dark.bgTertiary,
                handles: {
                    enabled: true,
                    backgroundColor: this.context.lightTheme ? "#f5f5f5" : "#f2f2f2",
                    borderColor: this.context.lightTheme ? "#555555" : "#999"
                },
                height: 16,
                margin: 0
            },
            credits: {
                enabled: false
            },
            xAxis: {
                gridLineWidth: 0,
                tickPixelInterval: 25,
                labels: {
                    enabled: false
                },
                crosshair: false,
                lineWidth: 0,
                tickWidth: 0,
                minPadding: 0,
                maxPadding: 0,
                min: min,
                max: max,
                plotBands: this.convertBands(graphBands),
                events: {
                    setExtremes: (e) => {
                        if(e.trigger === "navigator" || e.trigger === "pan") this.updateView(e.min, e.max, true);
                    }
                },
            },
            yAxis: {
                zoomEnabled: false,
                gridLineWidth: 0,
                tickPixelInterval: 25,
                labels: {
                    enabled: false
                },
                minPadding: 0,
                maxPadding: 0,
                visible: true,
                text: ''
            },
            scrollbar: {
                enabled: true,
                height: 0,
                liveRedraw: true,
            },
            legend: {
                enabled: false
            },
            series: [{
                data: edited,
                tooltip: {
                    valueDecimals: 1,
                    valueSuffix: ''
                },
                lineWidth: 0.8,
                lineColor: "#fe6500",
                states: {
                    hover: {
                        enabled: false
                    }
                },
                events: {
                    click: (event) => {
                        this.findAndOpenMapFromX(event.point.x);
                    }
                },
            }, {
                data: formatted,
                tooltip: {
                    valueDecimals: 1,
                    valueSuffix: ''
                },
                lineWidth: 1.4,
                lineColor: this.context.lightTheme ? "#888" : "#fff",
                states: {
                    hover: {
                        enabled: false
                    }
                },
                events: {
                    click: (event) => {
                        this.findAndOpenMapFromX(event.point.x);
                    }
                },
            }],
            title: {
                text: ""
            },
            tooltip: {
                enabled: false,
                followTouchMove: false
            }
        }
    };

    formatData = (mode) => {
        const regex = new RegExp(".{"+mode+"}", "g");
        let formatted = this.context.calibrationData.match(regex);
        let edited = this.props.modifiedCalibrationData ? this.props.modifiedCalibrationData.match(regex) : formatted;

        formatted = formatted.map((val, index) => {

            if(this.state.loHi){
                const array_chunks = (array, chunk_size) =>
                    Array(Math.ceil(array.length / chunk_size))
                        .fill()
                        .map((_, index) => index * chunk_size)
                        .map(begin => array.slice(begin, begin + chunk_size));

                val = array_chunks(val, 2);
                val = val.reverse();
                val = val.join("");
            }

            val = parseInt(val, 16);

            if(this.state.signed === true){
                if(mode === 2) if(val > 0X80) val = -(0XFF - val + 1);
                if(mode === 4) if(val > 0X8000) val = -(0XFFFF - val + 1);
                if(mode === 8) if(val > 0X80000000) val = -(0XFFFFFFFF - val + 1);
            }

            return {x: index, y: val};
        });

        edited = edited.map((val, index) => {
            if(this.state.loHi){
                const array_chunks = (array, chunk_size) =>
                    Array(Math.ceil(array.length / chunk_size))
                        .fill()
                        .map((_, index) => index * chunk_size)
                        .map(begin => array.slice(begin, begin + chunk_size));

                val = array_chunks(val, 2);
                val = val.reverse();
                val = val.join("");
            }

            val = parseInt(val, 16);

            if(this.state.signed === true){
                if(mode === 2) if(val > 0X80) val = -(0XFF - val + 1);
                if(mode === 4) if(val > 0X8000) val = -(0XFFFF - val + 1);
                if(mode === 8) if(val > 0X80000000) val = -(0XFFFFFFFF - val + 1);
            }

            return {x: index, y: val};
        });

        return {
            formatted,
            edited
        };
    };

    updateEdited(){
        this.chart.current.chart.update({
            series: [{
                data: this.formatData(this.state.mode).edited
            }, {
                data: this.formatData(this.state.mode).formatted
            }]
        });
    }


    /**
     * Conversions
     */

    bitConvert(x, c, n){
        return (x * c) / n;
    }

    calculateMax(min){
        return min + this.state.range;
    }

    convertBands = (bands, newMode, oldMode) => {
        return bands.map((band) => {
            return {
                ...band,
                from: this.bitConvert(band.from, oldMode ? oldMode : this.state.mode, newMode ? newMode : this.state.mode),
                to: this.bitConvert(band.to, oldMode ? oldMode : this.state.mode, newMode ? newMode : this.state.mode),
            }
        });
    };


    /**
     * Scrolling
     */

    handleScroll = (e) => {

        if(this.state.disableActions) return;

        if(e.path && e.path.some(elem => elem?.classList?.contains("highcharts-container"))){
            console.log(e)
            if(e.shiftKey) {
                this.zoom(e.wheelDelta > 0)
            } else {
                this.pan(e.wheelDelta > 0)
            }
        }
    };

    zoom = (positive) => {

        let factor = 0.90;
        if(positive) factor = 1.1;

        const offset = (this.state.range * factor) - this.state.range;

        let range = this.state.range + offset;

        let min = this.chart.current.chart.xAxis[0].min - (offset / 2);
        min = this.bitConvert(min, this.state.mode, this.state.mode);
        const max = this.calculateMax(min);

        if((range > 15000 && positive) || (range < 5 && !positive)) return;

        this.updateView(min, max);
        this.setState({ range });
    };

    pan = (positive) => {

        let factor = 0.1;
        if(positive) factor = -0.1;

        let offset = this.state.range * factor;

        let min = this.chart.current.chart.xAxis[0].min + offset;
        const max = this.calculateMax(min);

        if(min < 0 || max >= this.state.chartOptions.series[0].data.length) return;

        this.updateView(min, max);
    };


    /**
     * Chart Updates
     */

    updateMode = (mode) => {

        if(this.state.disableActions) return;
        if(this.state.mode === mode) return;

        this.setState({
            disableActions: true
        }, () => {
            this.setState({
                range: this.bitConvert(this.state.range, this.state.mode, mode)
            }, () => {
                const min = this.bitConvert(this.chart.current.chart.xAxis[0].min, this.state.mode, mode);
                const max = this.calculateMax(min);

                this.chart.current.chart.update({
                    series: [{
                        data: this.formatData(mode).edited
                    }, {
                        data: this.formatData(mode).formatted
                    }],
                    navigator: {
                        xAxis: {
                            plotBands: this.convertBands(this.chart.current.chart.navigator.xAxis.plotLinesAndBands.map(band => band.options), mode)
                        }
                    }
                });

                this.setState({
                    disableActions: false,
                    mode
                }, () => {
                    this.updateView(min, max);
                });
            })
        })
    };

    updateSelectedMap = (id) => {

        const band = this.convertBands(this.state.graphBands, this.state.mode, 4).filter(band => band.id === id)[0];

        if(band){
            const start = band.from;
            const end = band.to;

            const width = end - start;

            let min = start - (this.state.range / 2) + (width / 2);
            const max = this.calculateMax(min);

            this.updateView(min, max);
        }
    };

    updateView = (min, max, doNotSetExtremes) => {

        if(!doNotSetExtremes) this.chart.current.chart.xAxis[0].setExtremes(min, max);

        this.chart.current.chart.update({
            xAxis: [{
                plotBands: []
            }]
        });

        const openIds = this.context.windows.map(window => window.mapId);

        // Find bands that should be in view
        this.convertBands(this.state.graphBands, this.state.mode, 4).filter((band) => {
            return band.to > min && band.from < max;
        }).forEach((band) => {
            let borderWidth = openIds.includes(band.id) || this.props.modifiedMaps.includes(band.id) ? "1px" : 0
            if (this.props.selectedMapId && this.state.focusedMapId === band.id) borderWidth = "2px";
            this.chart.current.chart.xAxis[0].addPlotBand({
                ...band,
                borderWidth,
                borderColor: this.props.modifiedMaps.includes(band.id) ? "#fe6500" : this.context.lightTheme ? "#484848" : "#ffffff"
            });
        });
    };


    /**
     * Open Maps
     */
    openMap = () => {
        if(this.state.disableActions) return;
        this.context.openWindow(this.state.focusedMapId);
    };

    findAndOpenMapFromX = (x) => {
        if(this.state.disableActions) return;

        const map = this.state.calibrationZones.filter((zone) => { return this.bitConvert(zone.from) < x && x < this.bitConvert(zone.to) })[0];
        if(map) this.context.openWindow(map.id);
    };

    /**
     * Jump to maps
     */

    setFocusedMapId = focusedMapId => {
        this.setState({focusedMapId}, () => {
            this.context.setSelectedWindow(focusedMapId);
            this.updateSelectedMap(focusedMapId);
        })
    }

    toPreviousMap = (event) => {
        event.preventDefault();
        if(this.state.disableActions) return;

        if(this.state.focusedMapId){
            let previousMapIdx = null;
            this.state.calibrationZones.forEach((zone, i) => { if(zone.id === this.state.focusedMapId) previousMapIdx = i - 1 });

            if(previousMapIdx >= 1){
                const previousMap = this.state.calibrationZones[previousMapIdx].id;
                this.setFocusedMapId(previousMap);
            }
        }
    };

    toNextMap = (event) => {
        event.preventDefault();
        if(this.state.disableActions) return;

        if(this.state.focusedMapId){
            let nextMapIdx = null;
            this.state.calibrationZones.forEach((zone, i) => { if(zone.id === this.state.focusedMapId) nextMapIdx = i + 1 });

            if(nextMapIdx <= (this.state.calibrationZones.length - 2)) {
                const nextMap = this.state.calibrationZones[nextMapIdx].id;
                this.setFocusedMapId(nextMap);
            }
        }
    };

    toPreviousEditedMap = (event) => {
        event.preventDefault();
        if(this.state.disableActions) return;

        let maps = this.getMaps();
        let currentPosition = 0;

        if(this.state.focusedMapId){
            currentPosition = maps.filter((map) => { return map.id === this.state.focusedMapId })[0].calibrationStartAddress;
        }

        maps = maps.filter((map) => { return map.swModified && map.calibrationStartAddress < currentPosition });
        if(maps[0]){
            const nextEditedMap = maps.reduce((a, b) => {
                return Math.abs(b.calibrationEndAddress - currentPosition) < Math.abs(a.calibrationEndAddress - currentPosition) ? b : a;
            });

            this.setFocusedMapId(nextEditedMap.id);
        }
    };

    toNextEditedMap = (event) => {
        event.preventDefault();
        if(this.state.disableActions) return;

        let maps = this.getMaps();
        let currentPosition = 0;

        //console.log(this.state.focusedMapId)
        if(this.state.focusedMapId){
            console.log(maps.filter((map) => { return map.id === this.state.focusedMapId })[0])
            currentPosition = maps.filter((map) => { return map.id === this.state.focusedMapId })[0].calibrationStartAddress;
        }

        maps = maps.filter((map) => { return map.swModified && map.calibrationStartAddress > currentPosition });

        //console.log(currentPosition)
        //console.log(maps)
        if(maps[0]){
            const nextEditedMap = maps.reduce((a, b) => {
                return Math.abs(b.calibrationStartAddress - currentPosition) < Math.abs(a.calibrationStartAddress - currentPosition) ? b : a;
            });

            this.setFocusedMapId(nextEditedMap.id);
        }
    };


    /**
     *  Get maps
     */

    getMaps = () => {
        return this.context.folderStructure.reduce((accumulator, folder) => {
            return [ ...accumulator, ...folder.codeNames.map((map) => {
                let type = "";

                switch(map.type){
                    case "3d":
                        type = "3D";
                        break;
                    case "2d":
                        type = "2D";
                        break;
                    case "single":
                        type = "Single Value";
                        break;
                    default:
                        type = "";
                        break;
                }

                return {
                    id: map.id,
                    swModified: map.swModified,
                    title: map.title,
                    titleDescription: map.titleDescription,
                    mapSize: map.mapSize,
                    type: type,
                    calibrationStartAddress: parseInt(map.calibrationStartAddress),
                    calibrationEndAddress: parseInt(map.calibrationEndAddress)
                }
            })]
        }, []);
    };

    render() {
        if(!this.state.loading){
            return (
                <div className={`${styles.navigator} bgSecondary`}>
                     <span className={styles.handle} onMouseDown={this.props.activateNavigatorResize} onTouchStart={this.props.activateNavigatorResize} />
                     <div className={`${styles.tools} bgSecondary highlightSecondary`}>
                         {/* Bit Toggles */}
                         <div data-hint={this.context.text.hints.bitToggles}>
                             <button onClick={this.updateMode.bind(this, 2)} className={this.state.mode === 2 ? styles.active : null}>8</button>
                             <button onClick={this.updateMode.bind(this, 4)} className={this.state.mode === 4 ? styles.active : null}>16</button>
                             <button onClick={this.updateMode.bind(this, 8)} className={this.state.mode === 8 ? styles.active : null}>32</button>
                         </div>

                         <span className="highlightPrimary"/>

                         {/* Options */}
                         <button
                             onClick={() => { this.setState({ signed: !this.state.signed }) }}
                             data-hint={this.context.text.hints.signedUnsignedToggle}
                         >
                             <img src={require(`images/icons/signed${this.context.lightTheme ? "-light" : "-dark"}.svg`)} alt={this.state.loHi ? "hilo" : "lohi"} style={{height: 9, opacity: this.state.signed ? 1 : 0.5}} />
                         </button>
                         <button
                             onClick={() => { this.setState({ loHi: !this.state.loHi }) }}
                             disabled={this.state.mode < 3}
                             className={this.state.mode < 3 ? styles.disabled : null}
                             data-hint={this.context.text.hints.loHi}
                         >
                             <img src={require(`images/icons/${this.state.loHi ? "lohi" : "hilo"}${this.context.lightTheme ? "-light" : "-dark"}.svg`)} alt={this.state.loHi ? "hilo" : "lohi"} style={{height: 12}} />
                         </button>

                         <span className="highlightPrimary"/>

                         {/* Jump to maps */}
                         <div data-hint={this.context.text.hints.navigators}>
                             <button onClick={this.toPreviousEditedMap} title="Jump to previous edited map (Shift + Left)">
                                 <img src={require(`images/icons/bckbck${this.context.lightTheme ? "-light" : "-dark"}.svg`)} alt={"<<"} />
                             </button>
                             <button onClick={this.toPreviousMap} title="Jump to previous map (Ctrl/Cmd + Left)">
                                 <img src={require(`images/icons/bck${this.context.lightTheme ? "-light" : "-dark"}.svg`)} alt={"<"} />
                             </button>
                             <button onClick={this.toNextMap} title="Jump to next map (Ctrl/Cmd + Right)">
                                 <img src={require(`images/icons/fwd${this.context.lightTheme ? "-light" : "-dark"}.svg`)} alt={">"} />
                             </button>
                             <button onClick={this.toNextEditedMap} title="Jump to next edited map (Shift + Right)">
                                 <img src={require(`images/icons/fwdfwd${this.context.lightTheme ? "-light" : "-dark"}.svg`)} alt={">>"} />
                             </button>
                         </div>

                         <button onClick={this.props.toggleNavigator} className={styles.hide}>Hide</button>
                    </div>

                    <div className={styles.chartParent} data-hint={this.context.text.hints.hexViewer}>
                        <HighchartsReact
                            ref={this.chart}
                            constructorType={"stockChart"}
                            highcharts={Highstocks}
                            allowChartUpdate={true}
                            options={this.state.chartOptions}
                        />
                        <div className={styles.grid} style={{height: this.props.navigatorHeight - 13}} />
                    </div>
                </div>
            )
        } else {
            return false;
        }
    }
}

export default HexViewer;
